import { EXPLICIT_UNKNOWN_SOURCE_DISPLAY, isUndefined, schSourceType } from '@aloft/toolkit-shared';
import { Flex, Icon, Input, Tooltip, Typography, theme } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra, aloft/prefer-sound-ui-button
import { Button, Popover, PopoverContent, PopoverTrigger } from '@aloft/toolkit-sui/chakra';

import { useRef, useState } from 'react';
import { Controller, ControllerRenderProps, FieldError, FieldPath } from 'react-hook-form';

import { dispatchAdjustmentsPageAlertTrigger } from '../../../modules/alerts/dispatchAdjustmentsPageAlertTrigger.ts';
import { dispatchComparablesPageAlertTrigger } from '../../../modules/alerts/dispatchComparablesPageAlertTrigger.ts';
import { useAlertsRef } from '../../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { selectCommentaryV2 } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { PropertyGridForm } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import { PopoverInputHeader } from './shared/PopoverInputHeader.tsx';
import { TruncatedText } from './shared/TruncatedText.tsx';
import { usePropertyField } from './shared/usePropertyField.tsx';
import { usePropertyGridCellFocus } from './shared/usePropertyGridCellFocus.ts';
import {
  getSiteComboboxRows,
  siteComboboxClosedLabel,
  type SiteComboboxOption,
} from './siteComboboxOptions.ts';
// eslint-disable-next-line aloft/prefer-sound-ui-button
import {
  alertTargetFieldHoverSx,
  emptyFieldStyle,
  getEditableFieldTriggerAlertProps,
  popoverInputSx,
  sharedButtonStyle,
} from './styles.ts';
import { PropertyGridFieldProps } from './types.ts';

const IconElement = ({ isOpen, error }: { isOpen: boolean; error?: FieldError }) =>
  error ? (
    <Tooltip label={error.message}>
      <Icon icon="error-warning" color="alertError" />
    </Tooltip>
  ) : (
    <Icon icon={isOpen ? 'arrow-drop-up' : 'arrow-drop-down'} />
  );

const toOptions = (field: PropertyGridFieldProps['field']): SiteComboboxOption[] =>
  (field.options ?? []).map((option) => ({
    value: String(option.value),
    label: option.label,
  }));

export const EditableComboboxField = ({
  columnIndex,
  field,
  isArv = false,
  colSpan = 1,
  control,
  getValues,
  setValue,
  gridType,
}: PropertyGridFieldProps) => {
  const dispatch = useAppDispatch();
  const alertsRef = useAlertsRef();
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [inputFocused, setInputFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(
    null,
  ) as React.MutableRefObject<HTMLInputElement | null>;

  const options = toOptions(field);

  const {
    propertyId,
    propertyDataFieldName,
    selectedSourceFieldName,
    displayValue,
    triggerLabel: fieldTriggerLabel,
  } = usePropertyField({
    columnIndex,
    field,
    isArv,
    control,
    getValues,
  });

  const fieldRef = useRef<HTMLButtonElement>(null);
  const {
    isAlertHighlighted,
    isAlertStyled,
    alertStyleVariant,
    alertCellTooltip,
    clearAlertTarget,
  } = usePropertyGridCellFocus({
    cellKeyId: field.fieldName,
    propertyId,
    fieldRef,
    isArv,
  });

  const rows = getSiteComboboxRows(options, query, field.allowCustom === true);
  const activeIndex = rows.length === 0 ? 0 : Math.min(highlightIndex, rows.length - 1);

  const handleSetFieldValue = (
    onChange: ControllerRenderProps['onChange'],
    newValue?: string | number,
  ) => {
    if (isUndefined(newValue) || newValue === '') return;

    const parsedNewValue = field.toValue
      ? (field.toValue(newValue.toString(), propertyId) as string | number)
      : newValue;
    onChange(parsedNewValue);

    setValue(
      `${selectedSourceFieldName}.source` as FieldPath<PropertyGridForm>,
      schSourceType.enum.custom,
    );
    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
    if (gridType === 'AdjustmentsGrid' && field.manualAdjustmentKey) {
      dispatchAdjustmentsPageAlertTrigger(dispatch, alertsRef, alertsCommentary);
    }
    if (gridType === 'FeatureGrid') {
      dispatchComparablesPageAlertTrigger(dispatch, alertsRef, alertsCommentary);
    }
    clearAlertTarget();
  };

  return (
    <Controller
      name={propertyDataFieldName}
      control={control}
      render={({ field: { onChange, ref }, fieldState: { error } }) => {
        const storedValue = displayValue === undefined ? undefined : String(displayValue);
        const triggerLabel =
          fieldTriggerLabel ??
          siteComboboxClosedLabel(options, storedValue) ??
          (storedValue === EXPLICIT_UNKNOWN_SOURCE_DISPLAY ? storedValue : undefined);

        const commit = (stored: string, close: () => void) => {
          handleSetFieldValue(onChange, stored);
          setQuery('');
          setHighlightIndex(0);
          setInputFocused(false);
          close();
        };

        return (
          <Popover
            gutter={-32}
            variant="inline"
            isLazy
            matchWidth
            initialFocusRef={inputRef}
            onClose={() => {
              setQuery('');
              setHighlightIndex(0);
              setInputFocused(false);
            }}
          >
            {({ isOpen, onClose }) => {
              const handleOnClose = () => {
                if (document.activeElement instanceof HTMLElement) document.activeElement?.blur();
                onClose();
              };

              const { title, backgroundColor } = emptyFieldStyle(isUndefined(displayValue));
              const alertTargetProps = getEditableFieldTriggerAlertProps(
                isAlertStyled,
                alertStyleVariant,
                isAlertHighlighted,
              );

              return (
                <>
                  <PopoverTrigger>
                    <Button
                      {...sharedButtonStyle}
                      ref={(el) => {
                        ref(el);
                        (fieldRef as React.MutableRefObject<HTMLButtonElement | null>).current = el;
                      }}
                      title={alertTargetProps?.title ?? alertCellTooltip ?? title}
                      backgroundColor={alertTargetProps?.backgroundColor ?? backgroundColor}
                      border="none"
                      boxShadow={alertTargetProps?.boxShadow}
                      {...(isAlertHighlighted ? { filter: alertTargetFieldHoverSx.filter } : {})}
                      _hover={alertTargetProps?._hover ?? sharedButtonStyle._hover}
                      gridColumn={`span ${colSpan}`}
                    >
                      <Flex alignItems="center" justifyContent="space-between" width="100%">
                        <TruncatedText>{triggerLabel}</TruncatedText>
                        <IconElement isOpen={isOpen} error={error} />
                      </Flex>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent borderRadius="0" width="100%">
                    <PopoverInputHeader inputFocused={inputFocused}>
                      <Input
                        type="text"
                        ref={(instance) => {
                          inputRef.current = instance;
                        }}
                        value={query}
                        placeholderText="Search"
                        __dangerDoNotUseSx={popoverInputSx}
                        onFocus={() => setInputFocused(true)}
                        onBlur={() => setInputFocused(false)}
                        onChange={(value) => {
                          setQuery(value);
                          setHighlightIndex(0);
                        }}
                        onClear={() => {
                          setQuery('');
                          setHighlightIndex(0);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'ArrowDown' && rows.length > 0) {
                            event.preventDefault();
                            setHighlightIndex(Math.min(activeIndex + 1, rows.length - 1));
                          } else if (event.key === 'ArrowUp' && rows.length > 0) {
                            event.preventDefault();
                            setHighlightIndex(Math.max(activeIndex - 1, 0));
                          } else if (event.key === 'Enter' || event.key === 'NumpadEnter') {
                            event.preventDefault();
                            const row = rows[activeIndex];
                            if (row) commit(row.value, handleOnClose);
                          } else if (event.key === 'Escape') {
                            setInputFocused(false);
                            handleOnClose();
                          }
                        }}
                      />
                      <IconElement isOpen={isOpen} error={error} />
                    </PopoverInputHeader>
                    <Flex flexDirection="column" gap="1px" maxHeight="25vh" overflowY="auto">
                      {rows.map((row, index) => (
                        <Button
                          key={`${row.kind}-${row.value}`}
                          title={row.label}
                          backgroundColor={
                            index === activeIndex ? theme.colors.selected1 : theme.colors.surface1
                          }
                          {...sharedButtonStyle}
                          onMouseDown={(event) => event.preventDefault()}
                          onMouseEnter={() => setHighlightIndex(index)}
                          onClick={() => commit(row.value, handleOnClose)}
                        >
                          <Flex alignItems="center" width="100%">
                            <TruncatedText>
                              <Typography variant="body1">{row.label}</Typography>
                            </TruncatedText>
                          </Flex>
                        </Button>
                      ))}
                    </Flex>
                  </PopoverContent>
                </>
              );
            }}
          </Popover>
        );
      }}
    />
  );
};
